import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env';

export interface EmailDto {
  toAddresses: string[];
  subject: string;
  bodyHtml: string;
}

export interface AgentDto {
  userName: string;
  agencyNumber: string;
  agencyName: string;
  agencyContactName: string;
  contactEmailAddress: string;
  agencyAddress1: string;
  agencyAddress2?: string;
  agencyCity: string;
  agencyState: string;
  agencyZipCode: string;
  systemId?: string;
}

export interface CustomerDto {
  nameOfInsured: string;
  systemId: string;
}

export interface AgentUserDto {
  id: number;
  agentPk: number;
  systemId: string;
  isApprovedAgent: boolean;
}

export interface PolicyDto {
  policyNumber: string;
}

export interface AgencyDto {
  agencyPk: number;
  agencyNumber: string;
  agencyName: string;
  agencyContactName: string;
  agencyContactEmail: string;
  agencyAddress: string;
  agencySecondaryAddress: string;
  agencyCity: string;
  agencyState: string;
  agencyZipCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class InsureApiService {
  private readonly baseUrl = environment.ApiBaseUrl;

  constructor(private http: HttpClient) {}

  sendEmail(email: EmailDto): Observable<object> {
    return this.http.post(`${this.baseUrl}/Email`, email);
  }

  insertAgent(agent: AgentDto): Observable<object> {
    return this.http.post(`${this.baseUrl}/Agents`, agent);
  }

  getAgentBySystemId(systemId: string): Observable<AgentDto> {
    return this.http.get<AgentDto>(`${this.baseUrl}/Agents/${encodeURIComponent(systemId)}`);
  }

  approveAgent(agentUserId: number): Observable<object> {
    return this.http.put(`${this.baseUrl}/AgentUsers/${agentUserId}/approve`, {});
  }

  getAgentUsersBySystemId(systemId: string): Observable<AgentUserDto[]> {
    return this.http.get<AgentUserDto[]>(`${this.baseUrl}/AgentUsers/by-user/${encodeURIComponent(systemId)}`);
  }

  insertCustomer(customer: CustomerDto): Observable<object> {
    return this.http.post(`${this.baseUrl}/Customers`, customer);
  }

  deleteCustomer(systemId: string): Observable<object> {
    return this.http.delete(`${this.baseUrl}/Customers/${encodeURIComponent(systemId)}`);
  }

  getPolicies(policyNumber: string, zipCode: string): Observable<PolicyDto[]> {
    return this.http.get<PolicyDto[]>(
      `${this.baseUrl}/Policies?basePolicyNumber=${encodeURIComponent(policyNumber)}&zipCode=${encodeURIComponent(zipCode)}&dateOfLoss=`,
    );
  }

  getAgency(agencyNumber: string): Observable<AgencyDto> {
    return this.http.get<AgencyDto>(
      `${this.baseUrl}/Agencies/${encodeURIComponent(agencyNumber)}/`,
    );
  }

  getActivePolicyReportPdf(agentNo: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Reports/dacpolicies/${encodeURIComponent(agentNo)}/pdf`,
      { responseType: 'blob' },
    );
  }

  getLossRunPolicyDropdown(agencyNo: string): Observable<{ policyNumber: string; insured: string }[]> {
    return this.http.get<{ policyNumber: string; insured: string }[]>(
      `${this.baseUrl}/Reports/daclossrunpolicydropdown/${encodeURIComponent(agencyNo)}`,
    );
  }

  getLossRunReportPdf(policyNumber: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Reports/daclossrunclaims/${encodeURIComponent(policyNumber)}/pdf`,
      { responseType: 'blob' },
    );
  }

  getDecPagePolicyDropdown(agencyNo: string): Observable<{ policyNumber: string; insured: string }[]> {
    return this.http.get<{ policyNumber: string; insured: string }[]>(
      `${this.baseUrl}/Reports/daclossrunpolicydropdown/${encodeURIComponent(agencyNo)}`,
    );
  }

  getDecPageReportPdf(policyNumber: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/Reports/dacdecpage/${encodeURIComponent(policyNumber)}/pdf`,
      { responseType: 'blob' },
    );
  }
}
